import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'
import _ from 'lodash'
import { z } from 'zod'
import { getCurrentUserQueryKey } from './api-hooks'

export const QUERY_CACHE_KEY_PREFIX = 'pixee.query-cache.'

export const LEGACY_QUERY_CACHE_KEY = 'REACT_QUERY_OFFLINE_CACHE'

const PersistedCurrentUserSchema = z.object({ username: z.string() })

const getPersistedUsername = (client: PersistedClient) => {
  const currentUserQuery = client.clientState.queries.find(query => _.isEqual(query.queryKey, getCurrentUserQueryKey()))
  return PersistedCurrentUserSchema.safeParse(currentUserQuery?.state.data).data?.username
}

export type UserScopedPersister = Persister & {
  /** Register for the window's `storage` event; it only fires for changes made by other tabs. */
  handleStorageEvent: (event: StorageEvent) => void
}

const listQueryCacheKeys = (storage: Storage) =>
  Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
    (storageKey): storageKey is string => storageKey !== null && storageKey.startsWith(QUERY_CACHE_KEY_PREFIX)
  )

/**
 * Persists the query cache under a key that names the signed-in user, so one user's cached
 * responses never render for another user on the same browser. The user is resolved once, in
 * `restoreClient`: `PersistQueryClientProvider` subscribes `persistClient` only after restore
 * settles.
 *
 * A page stops persisting for the rest of its life once the session may belong to someone else,
 * and deletes its entry, because its later responses could then be another user's data saved under
 * this user's key. Every tab shares one session cookie, so another tab signing in as a different
 * user changes whose data this page fetches. That tab's restore deletes this page's entry, and its
 * first write adds another user's entry, and either one reaches `handleStorageEvent` and stops this
 * page. A cached
 * `['current-user']` naming a different user stops it too, which covers a sign-in that never loads
 * this app in another tab.
 */
export const createUserScopedPersister = ({
  storage,
  getUsername,
}: {
  storage: Storage
  getUsername: () => Promise<string | undefined>
}): UserScopedPersister => {
  let user: { username: string; key: string; persister: Persister } | undefined
  let isStopped = false

  const stop = () => {
    if (isStopped) {
      return
    }
    isStopped = true
    if (user) {
      storage.removeItem(user.key)
    }
  }

  // `createAsyncStoragePersister` throttles writes, so one queued before `stop` would otherwise
  // re-create the entry after it.
  const stoppableStorage = {
    getItem: (key: string) => storage.getItem(key),
    setItem: (key: string, value: string) => {
      if (!isStopped) {
        storage.setItem(key, value)
      }
    },
    removeItem: (key: string) => storage.removeItem(key),
  }

  return {
    restoreClient: async () => {
      storage.removeItem(LEGACY_QUERY_CACHE_KEY)

      const username = await getUsername().catch(() => undefined)
      if (!username) {
        return undefined
      }

      const key = QUERY_CACHE_KEY_PREFIX + username
      for (const otherUserKey of listQueryCacheKeys(storage).filter(storageKey => storageKey !== key)) {
        storage.removeItem(otherUserKey)
      }

      user = { username, key, persister: createAsyncStoragePersister({ storage: stoppableStorage, key }) }

      const client = await user.persister.restoreClient()
      const persistedUsername = client && getPersistedUsername(client)
      if (persistedUsername !== undefined && persistedUsername !== username) {
        storage.removeItem(key)
        return undefined
      }
      return client
    },
    persistClient: async persistedClient => {
      if (isStopped) {
        return
      }
      const persistedUsername = getPersistedUsername(persistedClient)
      if (user && persistedUsername !== undefined && persistedUsername !== user.username) {
        stop()
        return
      }
      await user?.persister.persistClient(persistedClient)
    },
    removeClient: async () => {
      await user?.persister.removeClient()
    },
    handleStorageEvent: event => {
      if (!user) {
        return
      }
      const isStorageCleared = event.key === null
      const isOwnEntryRemoved = event.key === user.key && event.newValue === null
      const isOtherUserEntryWritten =
        event.key !== user.key && !!event.key?.startsWith(QUERY_CACHE_KEY_PREFIX) && event.newValue !== null
      if (isStorageCleared || isOwnEntryRemoved || isOtherUserEntryWritten) {
        stop()
      }
    },
  }
}
